import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { projects, services, reviews } from '../mockData';
import { useSiteSettings } from '../context/SiteSettingsContext';

const Home = () => {
  const { settings, loading } = useSiteSettings();

  if (loading || !settings) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section 
        className="hero-bg relative h-screen flex items-center justify-center"
        style={{
          backgroundImage: `linear-gradient(rgba(10, 10, 10, 0.7), rgba(10, 10, 10, 0.7)), url('https://images.unsplash.com/photo-1544984243-ec57ea16fe25?w=1920')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundColor: '#0A0A0A'
        }}
      >
        <div className="container mx-auto px-4 text-center z-10">
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 fade-in-up">
            {(settings.home_hero_title || 'Transform Your Home').trim().split(' ').slice(0, -1).join(' ')}{' '}
            <span className="text-[#D4AF37] gold-glow">
              {(settings.home_hero_title || 'Transform Your Home').trim().split(' ').slice(-1)[0]}
            </span>
          </h1>
          <p className="text-2xl md:text-3xl text-[#D4AF37] mb-4 fade-in-up font-semibold italic">
            "{settings.slogan}"
          </p>
          <p className="text-xl md:text-2xl text-gray-300 mb-8 max-w-3xl mx-auto fade-in-up stagger-1">
            {settings.home_hero_subtitle}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center fade-in-up stagger-2">
            <Link to="/quote">
              <Button size="lg" className="bg-[#D4AF37] text-black hover:bg-[#B8941F] font-semibold text-lg px-8">
                Get Free Quote
                <ArrowRight className="ml-2" size={20} />
              </Button>
            </Link>
            <Link to="/gallery">
              <Button size="lg" variant="outline" className="border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black font-semibold text-lg px-8">
                View Our Work
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-[#1A1A1A]">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">Why Choose BuildBroz?</h2>
            <div className="w-20 h-1 bg-[#D4AF37] mx-auto"></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { title: '15 Years Experience', desc: 'Over a decade of expert remodeling and satisfied homeowners' },
              { title: 'Quality Craftsmanship', desc: 'Premium materials and attention to every detail' },
              { title: 'Local Expertise', desc: 'Proudly serving Cleveland and surrounding areas' }
            ].map((item, idx) => (
              <Card key={idx} className="bg-[#2A2A2A] border-gray-700 p-8 text-center hover:border-[#D4AF37] transition-all duration-300">
                <CheckCircle className="mx-auto mb-4 text-[#D4AF37]" size={48} />
                <h3 className="text-xl font-semibold text-white mb-3">{item.title}</h3>
                <p className="text-gray-400">{item.desc}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="py-20 bg-[#0A0A0A]">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">Our Services</h2>
            <div className="w-20 h-1 bg-[#D4AF37] mx-auto"></div>
            <p className="text-gray-400 mt-4 max-w-2xl mx-auto">
              Comprehensive remodeling services for your home renovation project
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.slice(0, 3).map((service) => (
              <Card 
                key={service.id} 
                className="service-card bg-[#1A1A1A] border-gray-800 overflow-hidden group cursor-pointer"
              >
                <div className="relative h-64 overflow-hidden">
                  <img 
                    src={service.image} 
                    alt={service.title} 
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] to-transparent"></div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-semibold text-white mb-3">{service.title}</h3>
                  <p className="text-gray-400 mb-4">{service.description}</p>
                  <ul className="space-y-2">
                    {service.features.slice(0, 3).map((feature, idx) => (
                      <li key={idx} className="flex items-center text-sm text-gray-400">
                        <CheckCircle size={16} className="text-[#D4AF37] mr-2" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Projects */}
      <section className="py-20 bg-[#1A1A1A]">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">Our Projects</h2>
            <div className="w-20 h-1 bg-[#D4AF37] mx-auto"></div>
            <p className="text-gray-400 mt-4 max-w-2xl mx-auto">
              Check back soon to see our completed remodeling projects
            </p>
          </div>
          <div className="text-center">
            <p className="text-gray-500 mb-6">We're currently building our project gallery. Check back soon to see our amazing work!</p>
            <Link to="/gallery">
              <Button className="bg-[#D4AF37] text-black hover:bg-[#B8941F] font-semibold">
                Visit Gallery
                <ArrowRight className="ml-2" size={18} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials Preview */}
      <section className="py-20 bg-[#0A0A0A]">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">What Our Clients Say</h2>
            <div className="w-20 h-1 bg-[#D4AF37] mx-auto"></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {reviews.slice(0, 3).map((review) => (
              <Card key={review.id} className="review-card bg-[#1A1A1A] border-gray-800 p-6">
                <div className="flex mb-4">
                  {[...Array(review.rating)].map((_, i) => (
                    <Star key={i} size={20} className="text-[#D4AF37] fill-[#D4AF37]" />
                  ))}
                </div>
                <p className="text-gray-300 mb-4 italic">"{review.comment}"</p>
                <div className="border-t border-gray-700 pt-4">
                  <p className="font-semibold text-white">{review.name}</p>
                  <p className="text-sm text-[#D4AF37]">{review.project}</p>
                </div>
              </Card>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to="/reviews">
              <Button className="bg-[#D4AF37] text-black hover:bg-[#B8941F] font-semibold">
                Read All Reviews
                <ArrowRight className="ml-2" size={18} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#1A1A1A]">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Remodel Your Home?
          </h2>
          <p className="text-xl text-gray-400 mb-8 max-w-2xl mx-auto">
            Let's discuss your remodeling project and bring your vision to life
          </p>
          <Link to="/quote">
            <Button size="lg" className="bg-[#D4AF37] text-black hover:bg-[#B8941F] font-semibold text-lg px-10">
              Get Your Free Quote Today
              <ArrowRight className="ml-2" size={22} />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;